import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { FloatInput } from 'ui-components/components/float-input';
import { ChangePasswordService } from './change-password-service';
import style from '../authentication.module.css';
import classNames from 'clsx';
import { LocalePicker } from '../locale-picker';

import { messages } from './messages';

export const ChangePasswordForm = ({ isReferral, passwordResetHash }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [buttonDisabled, setButtonDisabled] = useState(isReferral);
  const [passwordResetCompleted, setPasswordResetCompleted] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const intl = useIntl();

  const inProcessing = useCallback(() => {
    setButtonDisabled(true);
    setErrorMsg('');
    setSuccessMsg('');
  }, []);

  const setErrorMessage = useCallback((msg) => {
    setErrorMsg(msg);
    setButtonDisabled(false);
  }, []);

  const setSuccessMessage = useCallback((msg) => {
    setSuccessMsg(msg);
    setButtonDisabled(false);
    setPasswordResetCompleted(true);
  }, []);

  const onChangePasswordClicked = useCallback(() => {
    if (isReferral && !termsChecked) {
      return;
    }

    inProcessing();

    if (!password || (typeof password === 'string' && !password.trim)) {
      setErrorMessage(intl.formatMessage(messages.errorPleaseEnterNewPassword));
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage(intl.formatMessage(messages.errorPasswordDoesNotMatch));
      return;
    }

    ChangePasswordService.changePassword(password, passwordResetHash)
      .then(({ data }) => {
        if (!data) {
          // TODO: better error message here
          setErrorMessage(intl.formatMessage(messages.errorUnableToCommunicateWithServer));
          return;
        }
        if (!data.success) {
          setErrorMessage(data.message || intl.formatMessage(messages.errorWhileChangingPassword));
          return;
        }

        setSuccessMessage(messages.successPasswordHasBeenUpdated);
      })
      .catch(() => {
        setErrorMessage(intl.formatMessage(messages.errorWhileChangingPassword));
      });
  }, [confirmPassword, inProcessing, isReferral, intl, setErrorMessage, setSuccessMessage, termsChecked, password, passwordResetHash]);

  useEffect(() => {
    const checkForEnterKey = (event) => {
      if (event.key === 'Enter') {
        onChangePasswordClicked();
      }
    };
    document.body.addEventListener('keypress', checkForEnterKey);

    return () => {
      document.body.removeEventListener('keypress', checkForEnterKey);
    };
  }, [onChangePasswordClicked]);

  const onTermsClicked = () => {
    const updatedTermsChecked = !termsChecked;
    setTermsChecked(updatedTermsChecked);
    setButtonDisabled(!updatedTermsChecked);
  };

  const renderResetHeader = () => {
    return (
      <React.Fragment>
        <div className={style['auth-form-title']}>
          <FormattedMessage {...messages.resetTitle} />
        </div>
        <div className={style['auth-form-instructions']}>
          <FormattedMessage {...messages.resetInstructions} />
        </div>
      </React.Fragment>
    );
  };

  const renderSetupHeader = () => {
    return (
      <React.Fragment>
        <div className={style['auth-form-title']}>
          <FormattedMessage {...messages.setupTitle} />
        </div>
        <div className={style['auth-form-instructions']}>
          <FormattedMessage {...messages.setupInstructions} />
        </div>
      </React.Fragment>
    );
  };

  const renderChangeButtonMessage = () => {
    return isReferral ? <FormattedMessage {...messages.setupPassword} /> : <FormattedMessage {...messages.changePassword} />;
  };

  const renderFinePrint = () => {
    return (
      <div className={style['auth-fine-print']}>
        <Checkbox checked={termsChecked} className={style['auth-agreement']} onChange={onTermsClicked} />
        <span>
          <FormattedMessage
            defaultMessage='I have read & agree to the {termsOfService} & {privacyPolicy}.'
            description='changePassword.setupTerms'
            id='bJsqHG'
            values={{
              termsOfService: (
                <a href='https://www.shipstation.com/terms-of-service' rel='noopener noreferrer' target='_blank'>
                  <FormattedMessage {...messages.termsLink} />
                </a>
              ),
              privacyPolicy: (
                <a href='https://www.shipstation.com/privacy-policy' rel='noopener noreferrer' target='_blank'>
                  <FormattedMessage {...messages.privacyLink} />
                </a>
              ),
            }}
          />
        </span>
      </div>
    );
  };

  const renderErrorMessage = () => {
    if (!errorMsg) {
      return null;
    }
    return <div className={style['error-message']}>{errorMsg}</div>;
  };

  const renderSuccessMessage = () => {
    if (!successMsg) {
      return null;
    }
    return (
      <div className={style['success-message']}>
        <FormattedMessage {...successMsg} />
      </div>
    );
  };

  const passwordChanged = useCallback((newPassword) => setPassword(newPassword), []);
  const confirmPasswordChanged = useCallback((newConfirmPassword) => setConfirmPassword(newConfirmPassword), []);

  return (
    <div className={style['auth-form']}>
      {isReferral ? renderSetupHeader() : renderResetHeader()}
      {passwordResetCompleted === false && (
        <div className={style['auth-form-content']}>
          <div className={style['auth-textfield']}>
            <FloatInput
              isPassword
              label={<FormattedMessage {...messages.newPassword} />}
              onChange={passwordChanged}
              type='password'
              value={password}
            />
          </div>

          <div className={style['auth-textfield']}>
            <FloatInput
              isPassword
              label={<FormattedMessage {...messages.confirmPassword} />}
              onChange={confirmPasswordChanged}
              type='password'
              value={confirmPassword}
            />
          </div>

          {isReferral && renderFinePrint()}

          <Button className={style['auth-button']} disabled={buttonDisabled} onClick={onChangePasswordClicked} theme='primary'>
            {renderChangeButtonMessage()}
          </Button>
        </div>
      )}
      {renderErrorMessage()}
      {renderSuccessMessage()}

      <footer>
        <Button
          className={classNames(style['text-link'], style['change-password'])}
          disabled={buttonDisabled}
          href='/login'
          target=''
          theme='link'
        >
          <FormattedMessage {...messages.login} />
        </Button>
        <LocalePicker />
        <Button className={style['text-link']} disabled={buttonDisabled} href='https://www.shipstation.com/step1/' target='' theme='link'>
          <FormattedMessage {...messages.createAnAccount} />
        </Button>
      </footer>
    </div>
  );
};

ChangePasswordForm.propTypes = {
  passwordResetHash: PropTypes.string.isRequired,
  isReferral: PropTypes.bool.isRequired,
};
